import XLSX from 'xlsx-js-style';

// ── helpers ────────────────────────────────────────────────────────────────────
const colLetter = (n) => {
  let s = '';
  let i = n + 1;
  while (i > 0) {
    const rem = (i - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    i = Math.floor((i - 1) / 26);
  }
  return s;
};
const addr = (col, row) => `${colLetter(col)}${row}`; // 0-based col, 1-based row

// ── style factory ──────────────────────────────────────────────────────────────
const border = (color = 'd1d5db', style = 'thin') => ({
  top:    { style, color: { rgb: color } },
  bottom: { style, color: { rgb: color } },
  left:   { style, color: { rgb: color } },
  right:  { style, color: { rgb: color } },
});

const makeStyle = (bg, opts = {}) => ({
  fill: { patternType: 'solid', fgColor: { rgb: bg } },
  font: {
    bold:  opts.bold  || false,
    sz:    opts.sz    || 10,
    color: { rgb: opts.color || '111827' },
    name:  'Calibri',
  },
  alignment: {
    horizontal: opts.align  || 'left',
    vertical:   opts.valign || 'center',
    wrapText:   opts.wrap   || false,
  },
  border: opts.border || border(),
  ...(opts.numFmt ? { numFmt: opts.numFmt } : {}),
});

// Static styles
const ST = {
  title:        makeStyle('1e3a5f', { bold: true, sz: 13, color: 'FFFFFF', align: 'center', border: border('1e3a5f') }),
  feeLabelGray: makeStyle('e5e7eb', { bold: true, sz: 10, border: border('d1d5db') }),
  feeValGray:   makeStyle('e5e7eb', { sz: 10, align: 'right', numFmt: '#,##0.00', border: border('d1d5db') }),
  feeLabelYel:  makeStyle('fef9c3', { bold: true, sz: 10, border: border('d1d5db') }),
  feeValYel:    makeStyle('fef9c3', { sz: 10, align: 'right', numFmt: '#,##0.00', border: border('d1d5db') }),
  feeLabelBlk:  makeStyle('FFFFFF', { sz: 10, border: border('e5e7eb') }),
  feeValBlk:    makeStyle('FFFFFF', { sz: 10, border: border('e5e7eb') }),
  colHdr:       makeStyle('1e40af', { bold: true, sz: 9, color: 'FFFFFF', align: 'center', wrap: true, border: border('1e3a5f', 'medium') }),
  subText:      makeStyle('dbeafe', { bold: true, sz: 9 }),
  subNum:       makeStyle('dbeafe', { bold: true, sz: 9, align: 'right', numFmt: '#,##0.00', border: border('93c5fd') }),
  subBorder:    makeStyle('dbeafe', { bold: true, sz: 9, border: border('93c5fd') }),
  gtLabel:      makeStyle('fef3c7', { bold: true, sz: 10, align: 'center', border: border('d97706', 'medium') }),
  gtNum:        makeStyle('fef3c7', { bold: true, sz: 10, align: 'right', numFmt: '#,##0.00', border: border('d97706', 'medium') }),
  blankCell:    (bg = 'FFFFFF') => makeStyle(bg, { sz: 9, border: border('e5e7eb') }),
};

// ── main export ────────────────────────────────────────────────────────────────
// costeoData shape:
// {
//   id: string,          manifest ID
//   total_lbs: number,   calculated from package weights
//   packages: [{
//     consignatario: string,
//     pkg_number: string,     unique_package_id (ENC-... or IO-...)
//     peso_lbs: number,
//     type: 'ENCOMIENDA' | 'CASILLERO',
//     items: [{ descripcion, cantidad, valor_unitario, mercancias, is_placeholder }]
//   }]
// }
export function generateCosteoXLSX(costeoData) {
  const manifestId = costeoData.id;
  console.log('[XLSX] Generating for:', manifestId);
  console.log('[XLSX] total_lbs:', costeoData.total_lbs);
  console.log('[XLSX] Package count:', (costeoData.packages || []).length);

  const ws = {};
  const merges = [];
  const setCell = (col, row, cellObj) => { ws[addr(col, row)] = cellObj; };

  // ── ZONE 1: Fee table (rows 1–15) ─────────────────────────────────────────
  // Row 1: Title (merged A1:M1)
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } });
  setCell(0, 1, { v: `COSTEO MANIFIESTO  ${manifestId}`, t: 's', s: ST.title });
  for (let c = 1; c < 13; c++) setCell(c, 1, { t: 'z', s: ST.title });

  // Row numbers MUST NOT shift — K formula refs $B$2,$B$3 and M refs $B$14
  const feeTable = [
    // [row, label,          labelStyle,       valStyle,       value ]
    [2,  'Total LBS',        ST.feeLabelGray,  ST.feeValGray,  null  ],  // SUM formula set after data
    [3,  'Flete',            ST.feeLabelYel,   ST.feeValYel,   null  ],  // blank — user fills
    [4,  'Security Fee',     ST.feeLabelYel,   ST.feeValYel,   15.00 ],
    [5,  'Guía',             ST.feeLabelYel,   ST.feeValYel,   22.60 ],
    [6,  'Trámite',          ST.feeLabelYel,   ST.feeValYel,   70.00 ],
    [7,  'Trámite Total',    ST.feeLabelYel,   ST.feeValYel,   null  ],  // blank — user fills
    [8,  'Teledespacho',     ST.feeLabelYel,   ST.feeValYel,   50.00 ],
    [9,  'Delivery',         ST.feeLabelYel,   ST.feeValYel,   50.00 ],
    [10, 'Adimex',           ST.feeLabelYel,   ST.feeValYel,   198.73],
    [11, 'Desarrollo',       ST.feeLabelYel,   ST.feeValYel,   33.90 ],
    [12, 'Impresiones',      ST.feeLabelYel,   ST.feeValYel,   39.55 ],
    [13, 'Transporte',       ST.feeLabelYel,   ST.feeValYel,   60.00 ],
    [14, 'In land /lb',      ST.feeLabelYel,   ST.feeValYel,   3.35  ],  // $B$14 used in col M
    [15, '',                 ST.feeLabelBlk,   ST.feeValBlk,   null  ],  // blank separator
  ];

  for (const [row, label, lblSt, valSt, value] of feeTable) {
    setCell(0, row, { v: label, t: 's', s: lblSt });
    setCell(1, row, value !== null ? { v: value, t: 'n', s: valSt } : { t: 'z', s: valSt });
    for (let c = 2; c < 13; c++) setCell(c, row, { t: 'z', s: ST.blankCell() });
  }

  // ── ZONE 2: Column headers (row 16) ───────────────────────────────────────
  const HEADERS = [
    'Consignatario Persona Natural', // A
    'No de PK',                      // B
    'Cant',                          // C
    'Descripcion',                   // D
    'Mercancias',                    // E
    'Merc',                          // F
    'VALOR UNITARIO ($)',             // G
    'TOTAL ($)',                      // H
    'PESO (LBS)',                    // I
    'Valor FOB',                     // J
    'Valor CIF',                     // K
    'Impuesto',                      // L
    'In Land',                       // M
  ];
  HEADERS.forEach((h, c) => setCell(c, 16, { v: h, t: 's', s: ST.colHdr }));

  // ── ZONE 3: Data rows (starting row 17) ───────────────────────────────────
  // Each package block = TIER 2 item rows (first row carries consignatario/pkg_number) + TIER 3 subtotal row
  let currentRow = 17;
  const subtotalIRows = []; // for B2 SUM formula
  const subtotalHRows = []; // for grand total H
  const subtotalKRows = []; // for grand total K
  const subtotalLRows = []; // for grand total L
  const subtotalMRows = []; // for grand total M
  let itemGroupEven = false;

  for (const pkg of (costeoData.packages || [])) {
    itemGroupEven = !itemGroupEven;
    const items = pkg.items || [];
    const isCasillero = pkg.type === 'CASILLERO';

    console.log(`[XLSX] Package: ${pkg.pkg_number} | type: ${pkg.type} | peso: ${pkg.peso_lbs} | items: ${items.length}`);

    // ── TIER 2: ITEM ROWS ────────────────────────────────────────────────────
    // First item row carries consignatario (col A) and pkg_number (col B).
    // Subsequent item rows leave A and B blank.
    const firstItemRow = currentRow;

    // If no items, write one placeholder row so subtotal has something to reference
    const rowsToWrite = items.length > 0 ? items : [{ is_placeholder: true }];

    for (let itemIndex = 0; itemIndex < rowsToWrite.length; itemIndex++) {
      const item = rowsToWrite[itemIndex];
      const isFirstItem = itemIndex === 0;
      const rowBg = isCasillero ? 'F8F8F8' : (itemGroupEven ? 'F9FAFB' : 'FFFFFF');
      const txtSt = makeStyle(rowBg, { sz: 9, border: border('e5e7eb') });
      const numSt = makeStyle(rowBg, { sz: 9, align: 'right', numFmt: '#,##0.00', border: border('e5e7eb') });

      // A: consignatario on first item only
      setCell(0, currentRow, isFirstItem
        ? { v: pkg.consignatario || '', t: 's', s: txtSt }
        : { t: 'z', s: txtSt });

      // B: pkg_number on first item only
      setCell(1, currentRow, isFirstItem
        ? { v: String(pkg.pkg_number || ''), t: 's', s: txtSt }
        : { t: 'z', s: txtSt });

      // C: quantity — blank for casillero / placeholder
      const qtyRaw = item.quantity != null ? item.quantity : item.cantidad;
      const cantVal = parseFloat(qtyRaw);
      setCell(2, currentRow,
        (item.is_placeholder || isCasillero || qtyRaw == null || isNaN(cantVal))
          ? { t: 'z', s: numSt }
          : { v: cantVal, t: 'n', s: numSt }
      );

      // D: description (English field name first, Spanish fallback)
      setCell(3, currentRow, { v: item.description || item.descripcion || '', t: 's', s: txtSt });

      // E: mercancias
      setCell(4, currentRow, { v: item.mercancias || '', t: 's', s: txtSt });

      setCell(5, currentRow, { t: 'z', s: txtSt }); // F: blank

      // G: unit value — English field name first, Spanish fallbacks
      const uvRaw = item.unit_price != null ? item.unit_price
        : item.valor_unitario != null ? item.valor_unitario
        : item.unit_value != null ? item.unit_value
        : item.price;
      const uvVal = parseFloat(uvRaw);
      const uvBlank = item.is_placeholder || isCasillero || uvRaw == null || isNaN(uvVal);
      console.log('[XLSX item]', {
        descripcion: item.descripcion,
        description: item.description,
        unit_price: item.unit_price,
        valor_unitario: item.valor_unitario,
        resolvedValue: uvVal,
      });
      setCell(6, currentRow, uvBlank ? { t: 'z', s: numSt } : { v: uvVal, t: 'n', s: numSt });

      // H: line total — blank for casillero / placeholder
      setCell(7, currentRow,
        uvBlank
          ? { t: 'z', s: numSt }
          : { v: (cantVal || 0) * (uvVal || 0), t: 'n', s: numSt }
      );

      // I–M: blank on item rows
      for (let c = 8; c < 13; c++) setCell(c, currentRow, { t: 'z', s: numSt });

      currentRow++;
    }

    const lastItemRow = currentRow - 1;
    const subtotalRow = currentRow;

    // ── TIER 3: SUBTOTAL ROW ─────────────────────────────────────────────────
    // H: SUM of item H — blank for casillero (item H cells are blank)
    const hCell = (!isCasillero && firstItemRow <= lastItemRow)
      ? { t: 'n', f: `SUM(H${firstItemRow}:H${lastItemRow})`, s: ST.subNum }
      : { t: 'z', s: ST.subNum };

    // I: peso_lbs — populated for both types
    const weightLb = parseFloat(pkg.peso_lbs);
    const iCell = (!isNaN(weightLb) && weightLb > 0)
      ? { v: parseFloat(weightLb.toFixed(2)), t: 'n', s: ST.subNum }
      : { t: 'z', s: ST.subNum };

    // J: Valor FOB = H subtotal — blank for casillero
    const jCell = isCasillero
      ? { t: 'z', s: ST.subNum }
      : { t: 'n', f: `H${subtotalRow}`, s: ST.subNum };

    // K: Valor CIF
    const kCell = {
      t: 'n',
      f: `J${subtotalRow}+IF($B$2=0,0,(I${subtotalRow}/$B$2)*$B$3)`,
      s: ST.subNum,
    };

    // L: Impuesto — blank for casillero, formula for encomienda
    const lCell = isCasillero
      ? { t: 'z', s: ST.subNum }
      : { t: 'n', f: `K${subtotalRow}*0.145`, s: ST.subNum };

    // M: In Land — formula for both
    const mCell = { t: 'n', f: `IF(I${subtotalRow}="","",I${subtotalRow}*$B$14)`, s: ST.subNum };

    // Total cant (non-placeholder items only, with field name fallback)
    const totalCant = items
      .filter(i => !i.is_placeholder)
      .reduce((s, i) => {
        const q = i.quantity != null ? i.quantity : i.cantidad;
        return s + (parseFloat(q) || 0);
      }, 0);

    // Bug 4: log calculated JS values for verification
    const valorFOB = items
      .filter(i => !i.is_placeholder && !isCasillero)
      .reduce((s, i) => {
        const q = parseFloat(i.quantity != null ? i.quantity : i.cantidad) || 0;
        const u = parseFloat(
          i.unit_price != null ? i.unit_price
          : i.valor_unitario != null ? i.valor_unitario
          : i.unit_value != null ? i.unit_value
          : i.price
        ) || 0;
        return s + q * u;
      }, 0);
    const impuesto = isCasillero ? 0 : valorFOB * 0.145;
    const totalACobrar = valorFOB + impuesto + (!isNaN(weightLb) ? weightLb * 3.35 : 0);
    console.log('[Costeo]', pkg.consignatario, { valorFOB, fleteShare: 0, valorCIF: valorFOB, impuesto, totalACobrar });

    const centerSub = { ...ST.subBorder, alignment: { horizontal: 'center', vertical: 'center' } };

    setCell(0, subtotalRow, { v: '↳ subtotal', t: 's', s: ST.subBorder });
    setCell(1, subtotalRow, { t: 'z', s: ST.subBorder });
    setCell(2, subtotalRow, totalCant > 0
      ? { v: totalCant, t: 'n', s: centerSub }
      : { t: 'z', s: ST.subBorder });
    setCell(3, subtotalRow, { t: 'z', s: ST.subBorder });
    setCell(4, subtotalRow, { t: 'z', s: ST.subBorder });
    setCell(5, subtotalRow, { t: 'z', s: ST.subBorder });
    setCell(6, subtotalRow, { t: 'z', s: ST.subBorder });
    setCell(7,  subtotalRow, hCell);
    setCell(8,  subtotalRow, iCell);
    setCell(9,  subtotalRow, jCell);
    setCell(10, subtotalRow, kCell);
    setCell(11, subtotalRow, lCell);
    setCell(12, subtotalRow, mCell);

    subtotalIRows.push(subtotalRow);
    subtotalHRows.push(subtotalRow);
    subtotalKRows.push(subtotalRow);
    subtotalLRows.push(subtotalRow);
    subtotalMRows.push(subtotalRow);

    currentRow++;
  }

  // ── Grand total row ────────────────────────────────────────────────────────
  const grandTotalRow = currentRow;

  const sumRange = (rows, col) => {
    if (rows.length === 0) return '0';
    return rows.map(r => `${col}${r}`).join('+');
  };
  // SUM() for I and M — M can return "" (text); SUM ignores text, + with text → #VALUE!
  const iSumAddr = subtotalIRows.map(r => `I${r}`).join(',');
  const mSumAddr = subtotalMRows.map(r => `M${r}`).join(',');

  merges.push({ s: { r: grandTotalRow - 1, c: 0 }, e: { r: grandTotalRow - 1, c: 3 } });
  setCell(0, grandTotalRow, { v: 'TOTAL GENERAL', t: 's', s: ST.gtLabel });
  setCell(1, grandTotalRow, { t: 'z', s: ST.gtLabel });
  setCell(2, grandTotalRow, { t: 'z', s: ST.gtLabel });
  setCell(3, grandTotalRow, { t: 'z', s: ST.gtLabel });
  setCell(4, grandTotalRow, { t: 'z', s: ST.gtLabel });
  setCell(5, grandTotalRow, { t: 'z', s: ST.gtLabel });
  setCell(6, grandTotalRow, { t: 'z', s: ST.gtLabel });

  setCell(7, grandTotalRow, subtotalHRows.length > 0
    ? { t: 'n', f: sumRange(subtotalHRows, 'H'), s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  setCell(8, grandTotalRow, iSumAddr
    ? { t: 'n', f: `SUM(${iSumAddr})`, s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  setCell(9, grandTotalRow, subtotalHRows.length > 0
    ? { t: 'n', f: sumRange(subtotalHRows, 'J'), s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  setCell(10, grandTotalRow, subtotalKRows.length > 0
    ? { t: 'n', f: sumRange(subtotalKRows, 'K'), s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  setCell(11, grandTotalRow, subtotalLRows.length > 0
    ? { t: 'n', f: sumRange(subtotalLRows, 'L'), s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  // M grand total: SUM() — M cells can return "" text
  setCell(12, grandTotalRow, mSumAddr
    ? { t: 'n', f: `SUM(${mSumAddr})`, s: ST.gtNum }
    : { v: 0, t: 'n', s: ST.gtNum });

  // ── B2 = Total LBS — SUM of all subtotal I values ─────────────────────────
  // Uses formula so it stays live if user edits peso cells in Excel
  console.log('[XLSX] B2 total_lbs — subtotal I rows:', subtotalIRows);
  ws['B2'] = iSumAddr
    ? { t: 'n', f: `SUM(${iSumAddr})`, s: ST.feeValGray }
    : { v: costeoData.total_lbs || 0, t: 'n', s: ST.feeValGray };

  // ── Worksheet metadata ─────────────────────────────────────────────────────
  ws['!ref'] = `A1:${addr(12, grandTotalRow)}`;
  ws['!merges'] = merges;
  ws['!freeze'] = { xSplit: 0, ySplit: 16, topLeftCell: 'A17', activePane: 'bottomLeft' };
  ws['!cols'] = [
    { wch: 20 }, // A Consignatario
    { wch: 18 }, // B No de PK
    { wch: 8  }, // C Cant
    { wch: 22 }, // D Descripcion
    { wch: 30 }, // E Mercancias
    { wch: 7  }, // F Merc
    { wch: 12 }, // G VALOR UNITARIO
    { wch: 12 }, // H TOTAL $
    { wch: 10 }, // I PESO LBS
    { wch: 12 }, // J Valor FOB
    { wch: 12 }, // K Valor CIF
    { wch: 12 }, // L Impuesto
    { wch: 14 }, // M In Land
  ];
  ws['!rows'] = [
    { hpt: 24 }, // row 1: title
  ];

  // ── Build workbook and trigger download ────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Costeo');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Costeo_${manifestId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}
